import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Robot, ChatCircleDots, TicketIcon, ChartBar } from "@phosphor-icons/react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";

/**
 * Compact "Assistant IA" analytics card for the Dashboard.
 * Shows: sessions/30j, escalation rate, resolved by IA alone, median turns, daily volume.
 */
export default function ChatAnalyticsCard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/stats/chat").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);
  if (!d) return null;
  // Empty state : hide the card entirely when the chat hasn't been used yet on
  // the 30-day window. Prevents a distracting "all zeros" tile on the dashboard
  // and keeps the layout clean until the Assistant IA is actually needed.
  if (!d.sessions) return null;
  const rate = d.escalation_rate;
  const rateTone = rate < 20 ? "text-emerald-600" : rate < 45 ? "text-orange-500" : "text-destructive";
  return (
    <Card className="lg:col-span-3" data-testid="chat-analytics-card">
      <CardHeader>
        <CardTitle className="font-display flex items-center gap-2">
          <Robot size={20} weight="duotone" className="text-primary"/> Efficacité Assistant IA · 30 j
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <Tile icon={<ChatCircleDots size={16}/>} label="Sessions" value={d.sessions} tone="primary"/>
          <Tile icon={<Robot size={16}/>} label="Résolues par l'IA" value={d.solved_by_helio} tone="emerald"/>
          <Tile icon={<TicketIcon size={16}/>} label="Escaladées (ticket)" value={d.escalated} tone="orange"/>
          <Tile icon={<ChartBar size={16}/>} label="Taux d'escalade" value={`${rate}%`} tone={rateTone.replace('text-', '')}/>
        </div>
        <div className="text-xs text-muted-foreground mb-2 flex items-center gap-3 flex-wrap">
          <span>Longueur médiane : <b className="mono text-foreground">{d.median_turns}</b> échanges</span>
          <span>·</span>
          <span>Moyenne : <b className="mono text-foreground">{d.avg_turns}</b></span>
        </div>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={d.volume}>
            <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(x) => x.slice(5)}/>
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false}/>
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }}/>
            <Bar dataKey="sessions" name="Sessions" fill="hsl(222,84%,45%)"/>
            <Bar dataKey="escalated" name="Escaladées" fill="hsl(28,95%,53%)"/>
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function Tile({ icon, label, value, tone }) {
  const tones = {
    primary: "border-l-primary text-primary",
    emerald: "border-l-emerald-500 text-emerald-600",
    orange: "border-l-orange-500 text-orange-500",
    destructive: "border-l-destructive text-destructive",
  };
  const cls = tones[tone] || tones.primary;
  return (
    <div className={`border-l-4 pl-3 py-2 ${cls}`}>
      <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground flex items-center gap-1">{icon} {label}</div>
      <div className="font-display text-2xl font-bold mono text-foreground mt-1">{value}</div>
    </div>
  );
}
